import NiceModal, { bootstrapDialog, useModal } from "@ebay/nice-modal-react";
import { AreaBounds } from "cloud-core/spatial/Spatial";
import dayjs from "dayjs";
import { FormEvent, useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import useAsyncCallback from "../../../hooks/useAsyncCallback";
import useMediaSources from "../../../hooks/useMediaSources";
import { VizContext } from "../../../models/viz/operations/DataOperation";
import { useGetStoredFilesForMediaQuery } from "../../../store/api/kinesense";
import { DateFormats } from "../../../utilities/dates";
import { tryExtractErrorMessage } from "../../../utilities/helpers";
import { Notifications } from "../../../utilities/Notifications/Notifications";
import ThumbnailExtractor from "../../../utilities/ThumbnailExtractor";
import ConditionalSpinner from "../../ConditionalSpinner";
import { MediaSourceVideoFile } from "cloud-core/media/MediaSource";
import { getDisplayVideoAtTimestamp } from "../../../models/media/MediaSource";

export interface DownloadVideoFrameModalProps {
    context: VizContext;
    selection?: AreaBounds;
}

const DownloadVideoFrameModal = NiceModal.create((props: DownloadVideoFrameModalProps) => {
    const bounds = props.selection ?? [0, 0, 1, 1];
    const mediaId = props.context.view?.data?.hasAssociatedMedia && props.context.view.data.primaryMediaId;
    const { activeMediaSource: media } = useMediaSources(props.context.viewId, mediaId);

    const [fileName, setFileName] = useState(
        `${media.name}__${dayjs(props.context.view.cursor).format(
            DateFormats.fileNameYearMonthDaysWithTimeMilliseconds,
        )}`,
    );
    const [url, setUrl] = useState<string>(undefined);
    const [videoFile, setVideoFile] = useState<MediaSourceVideoFile | undefined>(undefined);

    const {
        data: storedFiles,
        isLoading: isLoadingStoredFile,
        isUninitialized: isUninitializedStoredFile,
    } = useGetStoredFilesForMediaQuery({ projectId: media?.projectId, mediaId: media?.mediaId });
    const hasLoadedFile =
        !isLoadingStoredFile && !isUninitializedStoredFile && videoFile != null && storedFiles != null;
    const isLoading = !hasLoadedFile || url === undefined;

    const modal = useModal();

    useEffect(() => {
        if (media != null && storedFiles != null) {
            const file = getDisplayVideoAtTimestamp(media, props.context.view.cursor);
            setVideoFile(file);
        }
    }, [media, storedFiles]);

    useAsyncCallback(
        async () => {
            // Generate image data URL from file and time stamp
            const storedFile = storedFiles.get(videoFile.fileId);
            const timestamp = props.context.view.cursor - (media?.startsAt ?? 0) - videoFile.startsAt;
            const newUrl = await ThumbnailExtractor.getThumbnail(storedFile.accessUrl.url, bounds, timestamp).catch(
                (e) => {
                    Notifications.notify(
                        "Error extracting section of video frame",
                        `The following error was encountered while attempting to extract a section of a video frame: ${tryExtractErrorMessage(
                            e,
                        )}`,
                    );
                },
            );

            if (newUrl) {
                window.URL.revokeObjectURL(url);
                setUrl(newUrl);
            }
        },
        [hasLoadedFile],
        {
            skip: !hasLoadedFile,
        },
    );

    useEffect(() => {
        // Cleanup data URL
        return () => window.URL.revokeObjectURL(url);
    }, [url]);

    function handleOnSubmit(e: FormEvent) {
        e.preventDefault();

        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();

        modal.hide();
    }

    return (
        <Modal centered {...bootstrapDialog(modal)} onHide={modal.hide} backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>
                    <h5 className="">Download image</h5>
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleOnSubmit}>
                <Modal.Body>
                    <ConditionalSpinner isLoading={isLoading}>
                        <div className="text-center">
                            <img className="img-fluid" src={url} alt="Image preview" />
                        </div>
                    </ConditionalSpinner>

                    <hr className="mt-4" />

                    <Form.Group>
                        <Form.Label>Output file name</Form.Label>
                        <Form.Control
                            required
                            type="text"
                            value={fileName}
                            onChange={(e) => setFileName(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={modal.hide}>
                        Cancel
                    </Button>
                    <span title="Download image">
                        <Button disabled={isLoading} type="submit">
                            Download
                        </Button>
                    </span>
                </Modal.Footer>
            </Form>
        </Modal>
    );
});

export default DownloadVideoFrameModal;
